import { useState, useEffect, useRef } from "react";
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
import "react-vertical-timeline-component/style.min.css";

import { motion, AnimatePresence, useAnimation, useInView } from "framer-motion";
import { FaCode, FaLaptopCode, FaGraduationCap } from "react-icons/fa";
import { useLanguage } from '../context/LanguageContext';

import careerData from "../data/careerData.json";

interface CareerItem {
  id: number;
  title: string;
  subtitle: string;
  date: string;
  description: string;
  tech: string[];
  type: 'beruf' | 'ausbildung' | string;
  icon: 'laptop' | 'code' | 'graduation' | string;
}



/**
 * Dynamic Career Timeline Component
 * @returns 
 */
const CareerTimelineDynamic: React.FC = () => {
  const [filter, setFilter] = useState("alle");
  const [data, setData] = useState<CareerItem[]>([]);
  const { t } = useLanguage();

  // Daten laden
  useEffect(() => {
    setData(careerData);
  }, []);

  // Filtern der Daten
  const filteredData = data.filter((item) => {
    if (filter === "alle") return true;
    return (
      item.type === filter ||
      item.tech.some((t: string) =>
        t.toLowerCase().includes(filter.toLowerCase())
      )
    );
  });

  // Scroll-Animation
  const ref = useRef<HTMLElement>(null);
  const controls = useAnimation();
  
  // Type assertion for careerData
  useEffect(() => {
    setData(careerData as unknown as CareerItem[]);
  }, []);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  // Scroll-Animation
  useEffect(() => {
    if (inView) controls.start("visible");
  }, [inView, controls]);

  // Animationen
  const fadeIn = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };
  const fadeTransition = {
    hidden: { opacity: 0, scale: 0.98 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
    exit: { opacity: 0, scale: 0.98, transition: { duration: 0.3 } },
  };

  // Icons nach Typ
  const getIcon = (type: string) => {
    switch (type) {
      case "laptop":
        return <FaLaptopCode />;
      case "code":
        return <FaCode />;
      case "graduation":
        return <FaGraduationCap />;
      default:
        return <FaCode />;
    }
  };

  // Farben nach Typ
  const getColor = (type: string) => {
    switch (type) {
      case "beruf":
        return "#4f46e5";
      case "ausbildung":
        return "#312e81";
      default:
        return "#4338ca";
    }
  };

  // Globale Filterbuttons
  const filters = [
    { key: "alle", label: t("about.ct.filter.all") },
    { key: "beruf", label: t("about.ct.filter.job") },
    { key: "ausbildung", label: t("about.ct.filter.training") },
  ];

  return (
    <section id="timeline" ref={ref} className="py-20 bg-neutral-950 text-neutral-100">
      <motion.h2
        className="text-3xl font-bold text-center mb-8 text-neutral-200"
        variants={fadeIn}
        initial="hidden"
        animate={controls}
      >
        {t('about.ct.title')}
      </motion.h2>

      {/* Globale Filter */}
      <div className="flex justify-center gap-3 mb-10 flex-wrap">
        {filters.map((f) => (
          <motion.button
            key={f.key}
            onClick={() => setFilter(f.key)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
              filter === f.key
                ? "bg-indigo-600 border-indigo-500 text-white"
                : "border-neutral-700 text-neutral-400 hover:border-neutral-500 hover:text-neutral-200"
            }`}
          >
            {f.label}
          </motion.button>
        ))}
      </div>

      {/* Timeline */}
      <div className="max-w-5xl mx-auto">
        <VerticalTimeline lineColor="#27272a">
          <AnimatePresence mode="wait">
            {filteredData.map((item, index) => (
              <motion.div
                key={item.id}
                variants={fadeTransition}
                initial="hidden"
                animate="visible"
                exit="exit"
                layout
              >
                <VerticalTimelineElement
                  contentStyle={{
                    background: "#18181b",
                    color: "#fff",
                    border: "1px solid #27272a",
                    boxShadow: "0 0 10px rgba(0,0,0,0.4)",
                    marginBottom: "2rem"
                  }}
                  contentArrowStyle={{ borderRight: "7px solid #27272a" }}
                  date={t(item.date)}
                  iconStyle={{ background: getColor(item.type), color: "#fff" }}
                  icon={getIcon(item.icon)}
                >
                  <motion.div
                    variants={fadeIn}
                    initial="hidden"
                    animate={controls}
                    className="transition-transform duration-300 hover:translate-y-[-2px]"
                  >
                    <h3 className="text-xl font-semibold">{t(item.title)}</h3>
                    <h4 className="text-sm text-neutral-400 mb-2">{t(item.subtitle)}</h4>
                    <p className="text-neutral-300">{t(item.description)}</p>

                    {/* Dynamische Tech-Tags als Filter */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {item.tech.map((t) => (
                        <motion.button
                          key={t}
                          onClick={() => setFilter(t)}
                          whileHover={{ scale: 1.05 }}
                          className={`text-xs px-2 py-1 rounded-full border border-neutral-700 transition-all ${
                            filter === t
                              ? "bg-indigo-600 text-white border-indigo-500"
                              : "text-indigo-400 hover:bg-neutral-800"
                          }`}
                        >
                          {t}
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                </VerticalTimelineElement>
              </motion.div>
            ))}
          </AnimatePresence>
        </VerticalTimeline>

        {filteredData.length === 0 && (
          <p className="text-center text-neutral-500 mt-8">
            {t('about.ct.noData')}
          </p>
        )}
      </div>
    </section>
  );
}

export default CareerTimelineDynamic;